/**
 * Sending each counting shot to the server (API-004, docs/phase-4-plan.md §4.2).
 *
 * The page never waits for this: a shot animates the moment Shoot is pressed, exactly as
 * before, and is sent alongside. The server plays the same shot itself and keeps the
 * result that counts (D-035). Checking is silent (D-039): nothing here is ever shown to
 * the player, whether a send works, is retried or is refused.
 *
 * The queue is not a second copy of the shots. The day's record in `potted.history.v1`
 * already holds every shot as it is taken; all this keeps is how many of them the server
 * has acknowledged (`potted.checked.v1`), so a shot taken offline, or before a reload, is
 * sent later, in order, from the record.
 *
 * What each reply means for the queue:
 *   - 200                   acknowledged: move on to the next shot
 *   - 409 out-of-order      the server expects a different shot number: carry on from it
 *   - 409 finished          the server already has a cleared run for today: stop
 *   - 409 not-today         the day is over by the server's clock: stop
 *   - 428 challenge         a new player: pass Turnstile (challenge.ts), then send again
 *   - 429 slow-down         too many from this address: later, like server trouble
 *   - 400/403/405/413/415   the server will never accept this: stop
 *   - anything else, or no reply at all: try again later, with a growing wait
 */
import type { ShotInput } from '@potted/sim';
import type { KeyValueStore } from '../settings/settings.ts';

export const CHECKED_KEY = 'potted.checked.v1';

export const shotsUrl = (dateKey: string): string => `/api/runs/${dateKey}/shots`;

/** What the server said about the run after the last shot it acknowledged. */
export interface ServerResult {
  readonly strokes: number;
  readonly cleared: boolean;
}

/** One day's progress: stored for today only, replaced when a new day starts. */
export interface Checked {
  readonly dateKey: string;
  /** Shots the server has acknowledged, counted from the first. */
  readonly sent: number;
  /** Set once the server has stopped taking shots for this day, for whatever reason. */
  readonly stopped?: boolean;
  readonly result?: ServerResult;
}

/** The waits between retries: 2 s, doubling, never longer than a minute. */
export const RETRY_FIRST_MS = 2000;
export const RETRY_MAX_MS = 60_000;

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const MAX_SHOTS = 300;
const MAX_STROKES = 1000;

const wholeNumber = (value: unknown, min: number, max: number): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;

function readResult(raw: unknown): ServerResult | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  if (!wholeNumber(r['strokes'], 0, MAX_STROKES) || typeof r['cleared'] !== 'boolean') {
    return undefined;
  }
  return { strokes: r['strokes'], cleared: r['cleared'] };
}

/** The stored progress, checked field by field (CLAUDE.md §10), or undefined. */
export function readChecked(text: string | null): Checked | undefined {
  if (text === null) return undefined;
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return undefined;
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const r = raw as Record<string, unknown>;
  const dateKey = r['dateKey'];
  if (typeof dateKey !== 'string' || !DAY_KEY.test(dateKey)) return undefined;
  if (!wholeNumber(r['sent'], 0, MAX_SHOTS)) return undefined;
  const stopped = r['stopped'];
  if (stopped !== undefined && stopped !== true) return undefined;
  const result = r['result'] === undefined ? undefined : readResult(r['result']);
  if (r['result'] !== undefined && result === undefined) return undefined;
  return {
    dateKey,
    sent: r['sent'],
    ...(stopped === true ? { stopped } : {}),
    ...(result === undefined ? {} : { result }),
  };
}

/** A shot reply from the server, if it is exactly what a correct server sends. */
export function readShotReply(body: unknown): (ServerResult & { readonly n: number }) | undefined {
  const result = readResult(body);
  if (result === undefined) return undefined;
  const n = (body as Record<string, unknown>)['n'];
  return wholeNumber(n, 1, MAX_SHOTS) ? { n, ...result } : undefined;
}

export type Post = (
  url: string,
  body: string,
  headers?: Readonly<Record<string, string>>,
) => Promise<{ status: number; body: unknown }>;

/** The request header for a Turnstile token; the server's CHALLENGE_HEADER. */
export const CHALLENGE_HEADER = 'x-potted-challenge';

const SITE_KEY = /^[A-Za-z0-9_-]{10,100}$/;

/** Checks per flush before waiting: a failing check is not retried in a tight loop. */
const MAX_CHALLENGES = 2;

/** The real network: same-origin JSON, with the player cookie, surviving a closing tab. */
export const fetchPost: Post = async (url, body, headers = {}) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { ...headers, 'content-type': 'application/json' },
    body,
    credentials: 'same-origin',
    cache: 'no-store',
    // A shot taken just before the tab closes still arrives.
    keepalive: true,
  });
  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch {
    parsed = undefined;
  }
  return { status: response.status, body: parsed };
};

export interface ShotSenderOptions {
  readonly dateKey: string;
  /** The day's counting shots so far, from the history record. */
  readonly shots: () => readonly ShotInput[];
  readonly storage: KeyValueStore | undefined;
  readonly post?: Post;
  /** Schedules a retry; injectable so tests need no real time. */
  readonly setTimer?: (run: () => void, ms: number) => void;
  /** Told whenever the server's view of the run changes. */
  readonly onResult?: (result: ServerResult) => void;
  /**
   * Runs the bot check the server asks a new player for, with the site key it sent,
   * and gives back the token (undefined if the check could not run). Without it, a new
   * player's shots wait and are retried.
   */
  readonly challenge?: (siteKey: string) => Promise<string | undefined>;
}

export interface ShotSender {
  /** Send whatever the server has not acknowledged yet. Safe to call at any time. */
  flush(): Promise<void>;
  /** The server's progress on the day, as far as this device knows. */
  checked(): Checked;
}

const PERMANENT = new Set([400, 403, 405, 413, 415]);

export function createShotSender(options: ShotSenderOptions): ShotSender {
  const { dateKey, storage } = options;
  const post = options.post ?? fetchPost;
  const setTimer =
    options.setTimer ??
    ((run: () => void, ms: number): void => {
      setTimeout(run, ms);
    });

  let stored: string | null;
  try {
    stored = storage?.getItem(CHECKED_KEY) ?? null;
  } catch {
    stored = null;
  }
  const previous = readChecked(stored);
  // Another day's progress is finished with: today starts from nothing.
  let state: Checked = previous?.dateKey === dateKey ? previous : { dateKey, sent: 0 };

  let running: Promise<void> | undefined;
  let again = false;
  let waitMs = RETRY_FIRST_MS;
  let retryScheduled = false;

  function save(next: Checked): void {
    state = next;
    try {
      storage?.setItem(CHECKED_KEY, JSON.stringify(next));
    } catch {
      // Full or blocked: the server still has what it acknowledged.
    }
  }

  function retryLater(): void {
    if (retryScheduled) return;
    retryScheduled = true;
    const ms = waitMs;
    waitMs = Math.min(waitMs * 2, RETRY_MAX_MS);
    setTimer(() => {
      retryScheduled = false;
      void flush();
    }, ms);
  }

  async function drain(): Promise<void> {
    let token: string | undefined;
    let challenges = 0;
    // A generous bound, so a server that keeps moving the goalposts cannot loop us.
    for (let requests = 0; requests < MAX_SHOTS * 2; requests++) {
      if (state.stopped === true) return;
      const shots = options.shots();
      const n = state.sent + 1;
      const shot = shots[n - 1];
      if (shot === undefined) return;

      let reply: { status: number; body: unknown };
      try {
        const headers: Record<string, string> =
          token === undefined ? {} : { [CHALLENGE_HEADER]: token };
        // A token is good for one check only.
        token = undefined;
        reply = await post(shotsUrl(dateKey), JSON.stringify({ n, shot }), headers);
      } catch {
        retryLater();
        return;
      }

      if (reply.status === 200) {
        const ok = readShotReply(reply.body);
        if (ok === undefined || ok.n !== n) {
          retryLater();
          return;
        }
        waitMs = RETRY_FIRST_MS;
        const result = { strokes: ok.strokes, cleared: ok.cleared };
        save({ dateKey, sent: n, ...(ok.cleared ? { stopped: true } : {}), result });
        options.onResult?.(result);
        continue;
      }

      const error =
        typeof reply.body === 'object' && reply.body !== null
          ? (reply.body as Record<string, unknown>)['error']
          : undefined;
      if (reply.status === 409 && error === 'out-of-order') {
        const expected = (reply.body as Record<string, unknown>)['expected'];
        // Only a number we can act on, and never one that repeats this very request.
        if (wholeNumber(expected, 1, MAX_SHOTS) && expected !== n) {
          save({ ...state, sent: expected - 1 });
          continue;
        }
        save({ ...state, stopped: true });
        return;
      }
      if (reply.status === 428 && error === 'challenge') {
        const siteKey = (reply.body as Record<string, unknown>)['siteKey'];
        if (
          options.challenge === undefined ||
          typeof siteKey !== 'string' ||
          !SITE_KEY.test(siteKey) ||
          challenges >= MAX_CHALLENGES
        ) {
          retryLater();
          return;
        }
        challenges++;
        token = await options.challenge(siteKey).catch(() => undefined);
        if (token === undefined) {
          retryLater();
          return;
        }
        continue;
      }
      if (reply.status === 409 && (error === 'finished' || error === 'not-today')) {
        save({ ...state, stopped: true });
        return;
      }
      if (PERMANENT.has(reply.status)) {
        save({ ...state, stopped: true });
        return;
      }
      // 409 conflict, 429, 5xx, a CPU limit hit on a cold start: the same shot, later.
      retryLater();
      return;
    }
  }

  function flush(): Promise<void> {
    if (running !== undefined) {
      // A shot taken while a send is in flight: pick it up when that one finishes.
      again = true;
      return running;
    }
    running = (async () => {
      do {
        again = false;
        await drain();
      } while (again && !retryScheduled);
      running = undefined;
    })();
    return running;
  }

  return { flush, checked: () => state };
}
